// Copyright (c) StrangeBee 2023

import React, { ReactElement, useEffect } from 'react';
import { ProfileOutput, PermissionOutput } from 'thehive-sdk';
import { useLocalMessage } from '@Helpers/';
import { ProfileForm, ProfileValuesForm } from '@Organisms/Profile/ProfileManage/ProfileForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { useGenericQuery } from '@Queries/genericQueries';
import client from '@Client';

interface ProfileManageProps {
    onFinish: (value: ProfileValuesForm) => void;
    triggerElem?: ReactElement;
    data?: ProfileOutput;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const ProfileManage = ({
    onFinish,
    triggerElem,
    data,
    visible,
    onVisibleChange,
}: ProfileManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm({
        isValidInit: !!data,
        fieldsTouchedInit: !!data,
    });

    const { data: permissionList = [], refetch: refetchPermissionList } = useGenericQuery<PermissionOutput[]>({
        fetchCallback: () => client.ProfileController.getPermissions(),
        key: ['list-permissions'],
        manual: true,
        fetchOnStart: false,
    });

    const onFinishHandler = ({ name = '', permissions = [], isAdmin = false }: ProfileValuesForm): void => {
        onFinish({ name: name.trim(), permissions, isAdmin });
    };

    useEffect(() => {
        form.setFieldsValue(data || {});
    }, [data, form]);

    const tradLabelKey = !!data ? 'edit' : 'create';

    return (
        <THDrawer
            title={i(`profile.${tradLabelKey}.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                visible && refetchPermissionList();
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
            visible={visible}
        >
            {(toggleDrawer) => (
                <ProfileForm
                    form={form}
                    isValid={isValid && fieldsTouched}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                    }}
                    onCancel={() => {
                        setFieldsTouched(false);
                        form.resetFields();
                        toggleDrawer();
                    }}
                    defaultValues={data}
                    permissionList={permissionList}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};
